import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Paper, Grid, Button, TextField, Alert,
  Snackbar, CircularProgress, Chip, Divider,
  Dialog, DialogTitle, DialogContent, DialogActions,
  IconButton, Collapse,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import { adminApi } from '../../api/adminApi';

// Field definitions for each provider's credential form
const PROVIDER_FIELDS = {
  Google: [
    { key: 'client_id', label: 'Client ID', required: true },
    { key: 'client_secret', label: 'Client Secret', required: true, sensitive: true },
  ],
  Facebook: [
    { key: 'client_id', label: 'App ID', required: true },
    { key: 'client_secret', label: 'App Secret', required: true, sensitive: true },
    { key: 'api_version', label: 'API Version', required: false, placeholder: 'v18.0' },
  ],
  LoginWithAmazon: [
    { key: 'client_id', label: 'Client ID', required: true },
    { key: 'client_secret', label: 'Client Secret', required: true, sensitive: true },
  ],
  SignInWithApple: [
    { key: 'client_id', label: 'Services ID', required: true },
    { key: 'team_id', label: 'Team ID', required: true },
    { key: 'key_id', label: 'Key ID', required: true },
    { key: 'private_key', label: 'Private Key (.p8)', required: true, multiline: true, sensitive: true },
  ],
  Microsoft: [
    { key: 'client_id', label: 'Application (Client) ID', required: true },
    { key: 'client_secret', label: 'Client Secret', required: true, sensitive: true },
    { key: 'oidc_issuer', label: 'OIDC Issuer URL', required: false, placeholder: 'https://login.microsoftonline.com/common/v2.0' },
  ],
};

function ProviderCard({ provider, onSave, onDelete, saving }) {
  const [expanded, setExpanded] = useState(false);
  const [credentials, setCredentials] = useState({});
  const [confirmDelete, setConfirmDelete] = useState(false);

  const fields = PROVIDER_FIELDS[provider.name] || [];

  const handleFieldChange = useCallback((key, value) => {
    setCredentials(prev => ({ ...prev, [key]: value }));
  }, []);

  const handleSave = useCallback(() => {
    onSave(provider.name, credentials);
  }, [provider.name, credentials, onSave]);

  const handleDelete = useCallback(() => {
    setConfirmDelete(false);
    onDelete(provider.name);
  }, [provider.name, onDelete]);

  const requiredMissing = fields
    .filter(f => f.required)
    .some(f => !credentials[f.key]);

  return (
    <Paper variant="outlined" sx={{ mb: 2 }}>
      <Box
        sx={{
          p: 2, display: 'flex', alignItems: 'center', cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setExpanded(e => !e)}
      >
        <Box flex={1}>
          <Box display="flex" alignItems="center" gap={1}>
            <Typography variant="subtitle1" fontWeight={600}>
              {provider.displayName}
            </Typography>
            {provider.enabled ? (
              <Chip
                icon={<CheckCircleIcon />}
                label="Enabled"
                color="success"
                size="small"
                variant="outlined"
              />
            ) : (
              <Chip
                icon={<CancelIcon />}
                label="Disabled"
                size="small"
                variant="outlined"
              />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary">
            Type: {provider.type} | Cognito name: {provider.name}
          </Typography>
        </Box>
        <IconButton size="small">
          {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
        </IconButton>
      </Box>

      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {provider.enabled
              ? 'This provider is active. Update credentials or disable it below.'
              : 'Enter OAuth credentials to enable this provider for social login.'}
          </Typography>

          <Grid container spacing={2}>
            {fields.map(field => (
              <Grid item xs={12} sm={field.multiline ? 12 : 6} key={field.key}>
                <TextField
                  fullWidth
                  size="small"
                  label={field.label}
                  required={field.required}
                  placeholder={field.placeholder || ''}
                  multiline={!!field.multiline}
                  rows={field.multiline ? 4 : undefined}
                  type={field.sensitive && !field.multiline ? 'password' : 'text'}
                  value={credentials[field.key] || ''}
                  onChange={e => handleFieldChange(field.key, e.target.value)}
                />
              </Grid>
            ))}
          </Grid>

          <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            {provider.enabled && (
              <Button
                color="error"
                variant="outlined"
                size="small"
                onClick={() => setConfirmDelete(true)}
                disabled={saving}
              >
                Disable
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              onClick={handleSave}
              disabled={saving || requiredMissing}
            >
              {saving ? <CircularProgress size={18} /> : (provider.enabled ? 'Update' : 'Enable')}
            </Button>
          </Box>
        </Box>
      </Collapse>

      <Dialog open={confirmDelete} onClose={() => setConfirmDelete(false)}>
        <DialogTitle>Disable {provider.displayName}?</DialogTitle>
        <DialogContent>
          <Typography>
            This will remove {provider.displayName} as a login option. Users who signed up
            via {provider.displayName} will need to use a different login method or reset their password.
            Stored credentials will be deleted from Secrets Manager.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDelete(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Disable Provider
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

export default function SocialProvidersPage() {
  const [providers, setProviders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const loadProviders = useCallback(async () => {
    try {
      const data = await adminApi.getSocialProviders();
      setProviders(data.providers || []);
    } catch (err) {
      setToast({ open: true, message: `Failed to load providers: ${err.message}`, severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProviders();
  }, [loadProviders]);

  const handleSave = useCallback(async (providerName, credentials) => {
    setSaving(true);
    try {
      await adminApi.updateSocialProvider(providerName, credentials);
      setToast({ open: true, message: `${providerName} configured successfully.`, severity: 'success' });
      await loadProviders();
    } catch (err) {
      setToast({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [loadProviders]);

  const handleDelete = useCallback(async (providerName) => {
    setSaving(true);
    try {
      await adminApi.deleteSocialProvider(providerName);
      setToast({ open: true, message: `${providerName} disabled.`, severity: 'success' });
      await loadProviders();
    } catch (err) {
      setToast({ open: true, message: err.message, severity: 'error' });
    } finally {
      setSaving(false);
    }
  }, [loadProviders]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 800, mx: 'auto' }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>
        Social Login Providers
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Configure OAuth credentials for social login providers. Enabled providers will appear
        as login options on the tenant signup page. Credentials are stored securely in AWS Secrets Manager.
      </Typography>

      <Alert severity="info" sx={{ mb: 3 }}>
        After enabling a provider, users will see a &ldquo;Continue with {'{Provider}'}&rdquo; button
        on the signup page. Each provider requires OAuth app credentials from their developer console.
      </Alert>

      {providers.map(provider => (
        <ProviderCard
          key={provider.name}
          provider={provider}
          onSave={handleSave}
          onDelete={handleDelete}
          saving={saving}
        />
      ))}

      <Snackbar
        open={toast.open}
        autoHideDuration={4000}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast(t => ({ ...t, open: false }))}
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
